import {
  LiteratureSourcesResponse,
  ResearchChat,
  ResearchChatListResponse,
  CreateResearchChatData,
  ResearchMessagesResponse,
  SendMessageStreamRequest,
  RecommendedQuestionsResponse,
  HomeRecommendedQuestionsResponse,
} from '../../schemas/chat';
import api from '../../lib/api';
import type { AxiosResponse } from '../../lib/api';
import { streamMessage } from '../../lib/stream';

// Research API for literature search
export const researchAPI = {
  /**
   * Get available literature search sources
   * @returns Promise with list of available literature sources
   */
  getLiteratureSources: (): Promise<AxiosResponse<LiteratureSourcesResponse>> =>
    api.get<LiteratureSourcesResponse>('/api/v1/research/literature/sources'),

  /**
   * Get research chats with pagination and filtering
   * @param params Query parameters for pagination and filtering
   * @returns Promise with paginated list of research chats
   */
  getResearchChats: (params?: {
    page?: number;
    page_size?: number;
    include_archived?: boolean;
    search?: string;
    kb_ids?: string[];
  }): Promise<AxiosResponse<ResearchChatListResponse>> =>
    api.get<ResearchChatListResponse>('/api/v1/research/chats', {
      params: {
        ...params,
        kb_ids: params?.kb_ids?.length ? params.kb_ids.join(',') : undefined,
      },
    }),

  /**
   * Get a specific research chat by ID
   * @param chatId The ID of the research chat
   * @returns Promise with research chat details
   */
  getResearchChat: (chatId: string): Promise<AxiosResponse<ResearchChat>> =>
    api.get<ResearchChat>(`/api/v1/research/chats/${chatId}`),

  /**
   * Create a new research chat
   * @param data Research chat creation data
   * @returns Promise with created research chat
   */
  createResearchChat: (data: CreateResearchChatData): Promise<AxiosResponse<ResearchChat>> =>
    api.post<ResearchChat>('/api/v1/research/chats', data),

  /**
   * Update a research chat
   * @param chatId The ID of the research chat
   * @param data Partial update data
   * @returns Promise with updated research chat
   */
  updateResearchChat: (
    chatId: string,
    data: Partial<CreateResearchChatData>
  ): Promise<AxiosResponse<ResearchChat>> =>
    api.put<ResearchChat>(`/api/v1/research/chats/${chatId}`, data),

  /**
   * Delete a research chat
   * @param chatId The ID of the research chat
   * @returns Promise confirming deletion
   */
  deleteResearchChat: (chatId: string): Promise<AxiosResponse<{ message: string }>> =>
    api.delete<{ message: string }>(`/api/v1/research/chats/${chatId}`),

  /**
   * Get messages from a research chat with pagination
   * @param chatId The ID of the research chat
   * @param params Query parameters for pagination
   * @returns Promise with paginated messages
   */
  getResearchMessages: (
    chatId: string,
    params?: {
      limit?: number;
      before?: string;
    }
  ): Promise<AxiosResponse<ResearchMessagesResponse>> =>
    api.get<ResearchMessagesResponse>(`/api/v1/research/chats/${chatId}/messages`, { params }),

  /**
   * Stream a message response with literature search support
   * @param chatId The ID of the research chat
   * @param data Message data with literature options
   * @returns Promise with streaming response
   */
  streamResearchMessage: async <T extends SendMessageStreamRequest = SendMessageStreamRequest>(
    chatId: string,
    data: T
  ): Promise<Response> => {
    // Use Fetch API for streaming
    return streamMessage(chatId, data, '/api/v1/research/chats');
  },

  /**
   * Get recommended questions for a specific research chat
   * @param chatId The ID of the research chat
   * @returns Promise with recommended questions
   */
  getChatRecommendedQuestions: (
    chatId: string
  ): Promise<AxiosResponse<RecommendedQuestionsResponse>> =>
    api.get<RecommendedQuestionsResponse>(`/api/v1/research/chats/${chatId}/recommended-questions`),

  /**
   * Get recommended questions for home page (global recommendations)
   * @returns Promise with recommended questions and user context
   */
  getHomeRecommendedQuestions: (): Promise<AxiosResponse<HomeRecommendedQuestionsResponse>> =>
    api.get<HomeRecommendedQuestionsResponse>('/api/v1/research/chats/recommended-questions'),

  /**
   * Create an ephemeral chat session for knowledge base
   * Creates a temporary conversation for chatting with a knowledge base
   * @param knowledgeBaseId The ID of the knowledge base
   * @param optionalTitle Optional title for the conversation
   * @returns Promise with created research chat
   */
  createKnowledgeBaseChat: (
    knowledgeBaseId: string,
    optionalTitle?: string
  ): Promise<AxiosResponse<ResearchChat>> =>
    api.post<ResearchChat>('/api/v1/research/chats', {
      title: optionalTitle || `Knowledge Base: ${knowledgeBaseId}`,
      knowledge_base_ids: [knowledgeBaseId],
      literature_sources: [],
    }),

  /**
   * Stream a message in knowledge base chat context
   * @param chatId The ID of the research chat
   * @param data Message data with content and optional file IDs
   * @returns Promise with streaming response
   */
  streamKnowledgeBaseMessage: async <T extends SendMessageStreamRequest = SendMessageStreamRequest>(
    chatId: string,
    data: T
  ): Promise<Response> => {
    // Use Fetch API for streaming
    return streamMessage(chatId, data, '/api/v1/research/chats');
  },

  /**
   * Resume streaming a message that was interrupted
   * @param chatId The ID of the research chat
   * @param messageId The ID of the message to resume
   * @returns Promise with streaming response
   */
  resumeMessageStream: async (chatId: string, messageId: string): Promise<Response> => {
    const token = localStorage.getItem('access_token');
    const API_BASE_URL = process.env.REACT_APP_API_URL || '';
    const url = `${API_BASE_URL}/api/v1/research/chats/${chatId}/messages/${messageId}/stream`;

    const headers = new Headers();
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    headers.set('Content-Type', 'application/json');
    headers.set('Accept', 'text/event-stream');

    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    if (!response.body) {
      throw new Error('Response body is null');
    }

    return response;
  },
};
